import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TheatreListDocumentType } from '../../../../core/services/patient-appointment.service';

export interface ExportDayAppointmentsDialogData {
  appointmentTypes: Array<{
    type: string;
    selected: boolean;
    backgroundColor?: string;
    textColor?: string;
    borderColor?: string;
  }>;
  date: Date;
  /**
   * §3b decision 2 — "that dialog gains a document choice". Present only for the theatre-list
   * export; absent for the Excel day export, which has exactly one renderer and therefore
   * nothing to ask. The dialog shows the picker if and only if this is a non-empty list.
   */
  documentTypes?: Array<{ value: TheatreListDocumentType; label: string; hint?: string }>;
  /** Overrides the heading; the Excel export keeps the default. */
  title?: string;
  /** Overrides the explanatory line under the heading. */
  hint?: string;
}

/**
 * What the dialog resolves with. An OBJECT rather than the bare `string[]` it used to return,
 * because there are now two independent answers — which appointment types, and which document —
 * and returning a tuple-by-convention would make the second one easy to drop silently.
 */
export interface ExportDayAppointmentsDialogResult {
  appointmentTypeNames: string[];
  /** Undefined when the dialog was opened without a document choice. */
  documentType?: TheatreListDocumentType;
}

@Component({
  selector: 'app-export-day-appointments-dialog',
  templateUrl: './export-day-appointments-dialog.component.html',
  styleUrls: ['./export-day-appointments-dialog.component.scss']
})
export class ExportDayAppointmentsDialogComponent {
  appointmentTypes: ExportDayAppointmentsDialogData['appointmentTypes'];
  date: Date;

  documentTypes: NonNullable<ExportDayAppointmentsDialogData['documentTypes']>;
  title: string;
  hint: string;

  /**
   * Seeded from the first offered document, so the Export button is never disabled for want of a
   * choice the user has not been asked to reconsider. It is a rendering choice, not clinical
   * content — unlike anything in §3b's no-copy-forward rule, defaulting it costs nothing.
   */
  selectedDocumentType: TheatreListDocumentType | null;

  constructor(
    public dialogRef: MatDialogRef<ExportDayAppointmentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExportDayAppointmentsDialogData
  ) {
    this.appointmentTypes = data.appointmentTypes || [];
    this.date = data.date;
    this.documentTypes = data.documentTypes || [];
    this.title = data.title || "Export day's appointments";
    this.hint = data.hint
      || 'Choose which appointment types to include in the export. Only appointments (not events) are exported.';
    this.selectedDocumentType = this.documentTypes.length ? this.documentTypes[0].value : null;
  }

  get asksForDocumentType(): boolean {
    return this.documentTypes.length > 0;
  }

  get selectedTypes(): string[] {
    return this.appointmentTypes.filter(t => t.selected).map(t => t.type);
  }

  /** Both answers must be present — a document choice that was asked for cannot be skipped. */
  get canExport(): boolean {
    if (this.selectedTypes.length === 0) return false;
    if (this.asksForDocumentType && !this.selectedDocumentType) return false;
    return true;
  }

  export(): void {
    if (!this.canExport) {
      return;
    }
    const result: ExportDayAppointmentsDialogResult = {
      appointmentTypeNames: this.selectedTypes,
    };
    if (this.asksForDocumentType && this.selectedDocumentType) {
      result.documentType = this.selectedDocumentType;
    }
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  selectAll(): void {
    this.appointmentTypes.forEach(t => t.selected = true);
  }

  deselectAll(): void {
    this.appointmentTypes.forEach(t => t.selected = false);
  }
}
